<template>
  <div class="nav-lang">
    <div class="nav-lang__button-items">
      <BaseIconSvg
        width="24"
        height="24"
        name="language-planet"
        class="nav-lang__planet"
      />
      <span class="nav-lang__text">
        {{ currentLocale && currentLocale.code }}
      </span>
    </div>
    <ClientOnly>
      <div class="nav-lang__window-wrapper">
        <div class="nav-lang__window">
          <ul class="nav-lang__list">
            <li class="nav-lang__list-item">
              <nuxt-link
                v-if="currentLocale"
                :to="switchLocalePath(currentLocale && currentLocale.code)"
                class="nav-lang__link nav-lang__link--current"
              >
                {{ currentLocale && currentLocale.code }}
              </nuxt-link>
            </li>
            <li
              v-for="lang in availableLocales"
              :key="lang.code"
              class="nav-lang__list-item"
            >
              <nuxt-link
                :aria-disabled="isDisabled(lang.code)"
                :to="
                  !isDisabled(lang.code)
                    ? switchLocalePath(lang.code)
                    : undefined
                "
                :class="[
                  'nav-lang__link',
                  {
                    'nav-lang__link--disabled': isDisabled(lang.code),
                  },
                ]"
              >
                {{ lang.code }}
              </nuxt-link>
            </li>
          </ul>
        </div>
      </div>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
const switchLocalePath = useSwitchLocalePath()
interface Props {
  navbarColor?: string
  isTransparent?: boolean
  buttonsColor?: '#000033' | '#fff'
}

defineProps<Props>()
const { availableLocales, currentLocale, isDisabled } = useLanguageSwitcher()
</script>

<style lang="scss" scoped>
.nav-lang {
  $root: &;

  position: relative;
  color: inherit;
  cursor: default;

  &:hover,
  &:focus,
  &:focus-within {
    #{$root}__button-items {
      background-color: var(--navbar-item-hover-bg, var(--paper-100));
    }

    #{$root}__window {
      display: block;
    }
  }

  &__button-items {
    @include paragraph-s;

    position: relative;
    padding: 8px 16px;
    display: flex;
    gap: 6px;
    align-items: center;
    color: inherit;
    letter-spacing: inherit;
    border-radius: 30px;
    transition: background-color 0.2s ease;
    cursor: pointer;

    &:focus-visible {
      outline: none;
      border: 1px solid var(--cobalt-600);
    }

    &:hover {
      color: inherit;
      background-color: var(--navbar-item-hover-bg, var(--paper-100));
    }

    &:active {
      background-color: var(--navbar-item-active-bg, var(--paper-200));
    }
  }

  &__text {
    text-transform: uppercase;
  }

  &__planet {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    color: inherit;
    stroke: currentColor;
  }

  &__window-wrapper {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    padding: 20px;
  }

  &__window {
    @include shadow-large-strong;

    display: none;
    border-radius: 20px;
    padding: 20px;
    width: 100%;
    background-color: var(--white);
  }

  &__list {
    display: flex;
    flex-direction: column;
    row-gap: 4px;
  }

  &__list-item {
    display: block;
    text-wrap: nowrap;
    text-transform: uppercase;
    cursor: pointer;
    width: 70px;
    text-align: right;

    &:has(.nav-lang__link--disabled),
    &:has(.nav-lang__link--current) {
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  &__link {
    @include paragraph-s;

    padding: 8px 12px;
    display: block;
    height: 100%;
    width: 100%;
    color: var(--color-text);
    text-decoration: none;
    position: relative;

    &--disabled {
      color: var(--gray-400);
      cursor: not-allowed !important;
      pointer-events: none;
    }

    &--current {
      &::before {
        left: 0;
        top: 45%;
        transform: translateY(-50%);
        position: absolute;
        width: 20px;
        height: 20px;
        content: '';
        background-image: url('@/assets/icons/16-list-benefit-cobalt.svg');
        background-size: cover;
      }
    }

    &:hover {
      text-decoration: none;
      color: #85858a;
    }
  }
}
</style>
